import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  QrCode, ArrowLeft, Camera, CheckCircle, XCircle, User, 
  Home, Clock, AlertTriangle, Shield, CameraOff, RefreshCw,
  IdCard, Loader2, UserPlus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BrowserQRCodeReader } from '@zxing/browser';
import type { IScannerControls } from '@zxing/browser';
import { securityAPI, visitorRegistryAPI } from '../services/api';
import { walkInApi, walkInListFromResponse, walkInEffectiveStatus, WALK_IN_STATUS_BADGE, type WalkIn, type WalkInEffectiveStatus } from '../services/accessApi';
// Walk-in "Sin QR" (roadmap item E, 2026-09-16): guard registers a visitor
// without a QR; host approves from MyQR. Modal creates pending_approval codes.
import WalkInRegisterModal from '../components/scan/WalkInRegisterModal';
import { compressImageToDataUrl } from '../utils/imageCompress';
// Scan result cards per spec ADDENDUM 2026-09-08 (c) §2/§5 (Rulings 2–3):
// resident full card / employee+destination / visitor+validity. Backend payload
// contract owned by task qr_scan_cards_backend; normalization is defensive.
import ScanResultCard, { normalizeScanCard, type ScanCardModel } from '../components/scan/ScanResultCard';

interface ScanResult {
  id: string;
  // FIX 2026-09-09 (QR live testing): history entries must carry the same
  // entry_type the scan card showed (scan-card.js single source of truth),
  // not the legacy guest/provider->visitor/else-resident guess.
  type: 'resident' | 'employee' | 'visitor' | 'staff' | 'invalid';
  name: string;
  unit?: string;
  timestamp: string;
  status: 'allowed' | 'denied' | 'expired';
  photo?: string;
  /** WHO scanned (Fix 2): authenticated scanner name + role captured at scan time. */
  scannerName?: string;
  scannerRole?: string;
  /** Direction of the scan (2026-09-09): entrada (default) / salida. */
  direction?: 'entrada' | 'salida';
  /** Code origin (roadmap item E): 'walk_in' → "Sin QR" chip in history rows. */
  origin?: string;
}

// ES labels for history rows, aligned with scan-card entry types.
const HISTORY_TYPE_LABEL: Record<ScanResult['type'], string> = {
  resident: 'Residente',
  employee: 'Empleado',
  staff: 'Empleado',
  visitor: 'Visitante',
  invalid: 'Inválido',
};

function scannerRoleLabel(role?: string): string | undefined {
  if (!role) return undefined;
  const map: Record<string, string> = {
    admin: 'Administrador',
    sub_admin: 'Sub-administrador',
    seguridad: 'Seguridad',
    security: 'Seguridad',
    super_admin: 'Super Admin',
    directive: 'Directiva',
    resident: 'Residente',
  };
  return map[role] ?? role;
}

// es-VE style date/time for history rows: dd/mm hh:mm (2026-09-09).
function fmtScanDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

type ScanDirection = 'entrada' | 'salida';

type CameraState = 'starting' | 'active' | 'denied' | 'unavailable' | 'off';

// Native BarcodeDetector (Chrome/Android). When unavailable we fall back to a
// wasm-based ZXing reader (@zxing/browser) so iOS Safari can still scan.
const BarcodeDetectorCtor = (window as any).BarcodeDetector;
const barcodeDetectorSupported = typeof BarcodeDetectorCtor === 'function';

export default function VerifyQR() {
  const navigate = useNavigate();
  const { user, activeCondoId, memberships } = useAuth();
  // Scanner condo context (ADDENDUM 2026-09-14b Phase 2 FE): VQR2 user-identity
  // passes resolve against the condo the scanner UI is OPERATING IN. Same
  // resolution pattern as the rest of the scanner UI: active condo selection
  // first, then memberships, then the user's legacy condoId. Sent explicitly as
  // ?condoId= on every validate call — supersedes the backend fallback.
  const scannerCondoId =
    activeCondoId || memberships?.[0]?.condoId || user?.condoId || undefined;
  const scannerCondoName =
    memberships?.find((mm) => mm.condoId === scannerCondoId)?.condoName;
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  // Structured card for the last scan (resident/employee/visitor per spec).
  const [card, setCard] = useState<ScanCardModel | null>(null);
  // Phase 2 (Ruling 3(b)): unregistered visitor → the guard must capture the
  // visitor's ID document and POST /api/access/id-capture with the code.
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [scannedCondoId, setScannedCondoId] = useState<string | null>(null);
  const [idCaptureBusy, setIdCaptureBusy] = useState(false);
  const [idCaptureOk, setIdCaptureOk] = useState<string | null>(null);
  const [idCaptureError, setIdCaptureError] = useState<string | null>(null);
  // Recapture (Jaime 2026-09-11): "Tomar nueva foto" replaces an unclear ID
  // photo. Shows a small "Foto actualizada" confirmation after the card
  // refreshes with the new photo; cleared on modal close / next scan.
  const [photoUpdated, setPhotoUpdated] = useState(false);
  // FIX 2026-09-11 (Jaime: photo not visible after capture): keep the local
  // compressed data-URL of the just-captured ID photo so the modal can render
  // it IMMEDIATELY (optimistic thumbnail) while the re-validation refreshes
  // the canonical id_photo_url on the card. Cleared on next scan / close.
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([]);
  // Direction toggle (2026-09-09): ENTRADA default; sent as ?direction= to the
  // validate endpoint. Older backends without direction support ignore it.
  const [direction, setDirection] = useState<ScanDirection>('entrada');
  // Backend ALREADY_INSIDE result: person scanned ENTRADA and has no SALIDA yet.
  const [alreadyInside, setAlreadyInside] = useState(false);
  // Scan-result modal (Jaime 2026-09-11): every scan opens a blocking popup
  // with the result; camera decode loop and manual entry are gated while it is
  // open, and it only closes via the explicit close buttons. A ref mirrors the
  // state for the mount-only camera decode loops (same stale-closure pattern
  // handleScanRef fixes for direction).
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const resultModalOpenRef = useRef(false);
  useEffect(() => { resultModalOpenRef.current = resultModalOpen; }, [resultModalOpen]);
  // --- Walk-in "Sin QR" (roadmap item E, 2026-09-16) ---
  // Guard-side list of walk-in registrations for the scanner condo, with a
  // pending/approved/denied/expired filter. Timeout-cancelled rows surface
  // reason WALK_IN_TIMEOUT ("Cancelado (sin aprobación)").
  const [walkInModalOpen, setWalkInModalOpen] = useState(false);
  const [walkIns, setWalkIns] = useState<WalkIn[]>([]);
  const [walkInsLoading, setWalkInsLoading] = useState(false);
  const [walkInTab, setWalkInTab] = useState<WalkInEffectiveStatus>('pending_approval');
  const [walkInsError, setWalkInsError] = useState<string | null>(null);
  // Walk-in enhancement (Jaime 2026-09-16): lightbox zoom for the walk-in ID
  // photo thumbnails in the guard list (same pattern as QrAuditPage zoomSrc).
  const [walkInPhotoZoom, setWalkInPhotoZoom] = useState<string | null>(null);

  const loadWalkIns = useCallback(async () => {
    if (!scannerCondoId) return;
    setWalkInsLoading(true);
    setWalkInsError(null);
    try {
      const res = await walkInApi.list(scannerCondoId);
      setWalkIns(walkInListFromResponse(res.data));
    } catch (err: any) {
      // 404 = backend not deployed yet → quiet empty state, no scary error.
      // FIX 2026-09-18 (Jaime live bug): any other failure (e.g. transient
      // "Membership lookup failed") must NOT blank the day log — keep the
      // last good list and surface a non-blocking warning instead.
      if (err?.response?.status !== 404) {
        setWalkInsError(err?.response?.data?.error || err?.message || 'Error al cargar visitas sin QR');
      } else {
        setWalkIns([]);
      }
    } finally {
      setWalkInsLoading(false);
    }
  }, [scannerCondoId]);

  useEffect(() => { loadWalkIns(); }, [loadWalkIns]);

  const walkInTabs: { key: WalkInEffectiveStatus; label: string }[] = [
    { key: 'pending_approval', label: 'Pendientes' },
    { key: 'approved', label: 'Aprobados' },
    { key: 'denied', label: 'Rechazados' },
    { key: 'expired', label: 'Expirados' },
  ];
  // FIX 2026-09-18 (Jaime live bug): timeout-cancelled rows (pending > 15 min,
  // cancel_reason WALK_IN_TIMEOUT) mapped to no tab at all, so day-log entries
  // silently vanished after the sweep. They belong under "Expirados" (the
  // decision window expired without a decision); the distinct
  // "Cancelado (sin aprobación)" badge still identifies them.
  const filteredWalkIns = walkIns.filter((w) => {
    const eff = walkInEffectiveStatus(w);
    return eff === walkInTab || (walkInTab === 'expired' && eff === 'timeout_cancelled');
  });

  const [manualCode, setManualCode] = useState('');
  const [cameraState, setCameraState] = useState<CameraState>('starting');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastDecodedRef = useRef<string>('');
  const lastDecodedAtRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const tickTimerRef = useRef<number | null>(null);
  const zxingControlsRef = useRef<IScannerControls | null>(null);

  // Handle QR code scan - calls the real validation endpoint
  // GET /api/qr-access/validate/:code (invalid/expired/revoked codes return 404
  // with { valid: false, reason }).
  // opts.refreshOnly (FIX 2026-09-14): photo-ID refresh revalidations pass refreshOnly so the
  // backend does NOT record a scan event (previously these re-runs logged a bogus DENEGADO
  // ALREADY_INSIDE scan). Genuine scans are unchanged.
  const handleScan = useCallback(async (codeOverride?: string, opts?: { refreshOnly?: boolean }) => {
    const scanCode = (codeOverride ?? manualCode).trim();
    if (!scanCode) return;
    setManualCode('');

    try {
      // ?condoId= (ADDENDUM 2026-09-14b): explicit scanner-condo resolution for
      // VQR2 passes (and manual 8-digit user entry). Older backends ignore it.
      const response = await securityAPI.verifyCode(scanCode, direction, opts?.refreshOnly === true, scannerCondoId);
      // Envelope: { success, valid: true, data: <access_codes row>, travel_warning }
      const row = response.data?.data ?? {};
      const isValid = response.data?.valid === true;
      // Remember the code + condo so the ID-capture flow (Ruling 3(b)) can
      // resolve the visitor server-side via the scanned access code.
      setScannedCode(scanCode);
      setIdCaptureOk(null);
      setIdCaptureError(null);
      setCapturedPreview(null);
      setPhotoUpdated(false);
      setAlreadyInside(false);
      setScannedCondoId(row?.condo_id || row?.condoId || user?.condoId || null);
      const now = new Date().toISOString();
      // ALREADY_INSIDE (BackendDev 2026-09-09): explicit result/reason_code or a
      // Spanish/English reason string. Graceful: simply not shown when the
      // backend doesn't send it yet.
      const insideHit =
        row?.result === 'ALREADY_INSIDE' ||
        row?.reason_code === 'ALREADY_INSIDE' ||
        /already.?inside|ya\s+registr(ó|o).*entrada|ya\s+se\s+encuentra/i.test(
          String(row?.reason ?? row?.message ?? ''),
        );
      setAlreadyInside(isValid && insideHit);
      // Direction resolution (2026-09-09 contract): the payload's `direction`
      // (BackendDev) wins; fall back to the scanner's toggle selection.
      const rowDirRaw = String(row?.direction ?? row?.scan_direction ?? row?.dir ?? '').toLowerCase();
      const dirResolved: ScanDirection =
        rowDirRaw === 'salida' || rowDirRaw === 'out' || rowDirRaw === 'exit'
          ? 'salida'
          : rowDirRaw === 'entrada' || rowDirRaw === 'in' || rowDirRaw === 'entry'
            ? 'entrada'
            : direction;
      // Spec ADDENDUM 2026-09-08 (c): typed scan-result card (resident/employee/
      // visitor) driven by the enriched backend payload; falls back gracefully
      // when the backend has not deployed the new fields yet.
      const cardModel = normalizeScanCard(row, { status: isValid ? 'allowed' : 'denied', timestamp: now, direction: dirResolved });
      setCard(cardModel);
      // FIX 2026-09-09: history entry derives type/label/address from the SAME
      // normalized card model the scan card renders — no more "resident · N/A"
      // for employees. Falls back to legacy guess only if kind is unknown.
      const result: ScanResult = {
        id: row?.id || scanCode,
        type: cardModel.kind === 'unknown' ? 'resident' : cardModel.kind,
        name: cardModel.name || row?.visitor_name || row?.name || 'Usuario',
        unit: cardModel.address || row?.unit,
        // 2026-09-09: keep the subject photo in the history entry when the
        // normalized card provided one (thumbnail in Historial de Escaneos).
        photo: cardModel.photoUrl,
        timestamp: now,
        status: isValid ? 'allowed' : 'denied',
        scannerName: user?.name,
        scannerRole: user?.role,
        direction: dirResolved,
        origin: cardModel.origin,
      };
      setLastScan(result);
      setScanHistory(prev => [result, ...prev].slice(0, 10));
      // EVERY scan (including denials) opens the blocking result modal.
      setResultModalOpen(true);
    } catch (err: any) {
      const reason = err?.response?.data?.reason || err?.response?.data?.error || '';
      const expired = /expired/i.test(reason);
      const now = new Date().toISOString();
      const result: ScanResult = {
        id: scanCode,
        type: 'invalid',
        name: expired ? 'Código Expirado' : 'Código Inválido',
        timestamp: now,
        status: expired ? 'expired' : 'denied',
        scannerName: user?.name,
        scannerRole: user?.role,
        direction,
      };
      setAlreadyInside(false);
      setCard(normalizeScanCard(undefined, {
        status: expired ? 'expired' : 'denied',
        reason: reason || undefined,
        timestamp: now,
      }));
      setLastScan(result);
      setScanHistory(prev => [result, ...prev].slice(0, 10));
      setResultModalOpen(true);
    }
  }, [manualCode, direction, scannerCondoId, user?.condoId, user?.name, user?.role]);

  // FIX 2026-09-11 (direction stale closure): the camera decode loop below is
  // registered once inside a mount-only useEffect, so it kept calling the
  // startCamera/handleScan closure from the FIRST render — which had
  // direction='entrada' baked in. Toggling Salida had no effect on camera
  // scans. Route camera callbacks through a ref that always points at the
  // latest handleScan.
  const handleScanRef = useRef(handleScan);
  useEffect(() => {
    handleScanRef.current = handleScan;
  }, [handleScan]);

  // ID document capture (Ruling 3(b)): file/camera pick → data-URL →
  // POST /access/id-capture { condoId, image, code }. Scanner roles only —
  // the backend 403s residents/employees.
  const handleIdFile = useCallback(async (file: File | undefined | null) => {
    if (!file) return;
    // FIX 2026-09-09 (HTTP 413 on "Capturar ID"): phone photos exceeded the
    // upload size limit. Compress client-side instead of rejecting — downscale
    // to 1600px max dimension, JPEG q0.8 (~200-400 KB typical), EXIF stripped
    // implicitly by the canvas re-encode. Only truly absurd inputs (>25 MB
    // raw) are still rejected.
    if (file.size > 25 * 1024 * 1024) {
      setIdCaptureError('La imagen supera el máximo de 25 MB.');
      return false;
    }
    if (!scannedCondoId) {
      setIdCaptureError('No se pudo determinar el condominio del escaneo.');
      return false;
    }
    setIdCaptureBusy(true);
    setIdCaptureError(null);
    try {
      const dataUrl = await compressImageToDataUrl(file, { maxDimension: 1600, quality: 0.8 });
      const res = await visitorRegistryAPI.captureId({
        condoId: scannedCondoId,
        image: dataUrl,
        code: scannedCode || undefined,
      });
      const doc = res.data?.data ?? res.data;
      setIdCaptureOk(doc?.id || 'ok');
      // Optimistic render (2026-09-11): show what was just captured right
      // away, independent of the validate refresh below.
      setCapturedPreview(dataUrl);
      return true;
    } catch (err: any) {
      // Robustness: if the server limit is still lower than our compressed
      // output, surface a clear message instead of a raw 413.
      if (err?.response?.status === 413) {
        setIdCaptureError('La imagen sigue siendo demasiado grande para el servidor. Intenta de nuevo.');
      } else {
        setIdCaptureError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error al registrar la identificación');
      }
      return false;
    } finally {
      setIdCaptureBusy(false);
    }
  }, [scannedCode, scannedCondoId]);

  // "Tomar nueva foto" (Jaime 2026-09-11): guard recaptures an unclear ID
  // photo for a person who ALREADY has one. Reuses the exact capture flow
  // (compress → POST /access/id-capture), then re-validates the scanned code
  // so the modal card refreshes with the NEW photo — the backend scan card
  // serves the latest document by captured_at DESC (latestIdPhotoDocId), so
  // no backend change is needed.
  const handleRecaptureId = useCallback(async (file: File | undefined | null) => {
    if (!scannedCode) return;
    const ok = await handleIdFile(file);
    if (!ok) return;
    setPhotoUpdated(false);
    await handleScanRef.current(scannedCode, { refreshOnly: true });  // FIX 2026-09-14: no scan event on photo refresh
    setPhotoUpdated(true);
  }, [handleIdFile, scannedCode]);

  // FIX 2026-09-11 (Jaime: photo not visible after FIRST capture): the
  // no-photo flow ("escanear doc") only uploaded the image and never
  // re-validated, so the modal card kept showing the "escanear documento"
  // prompt with no photo. After a successful upload, re-validate the scanned
  // code so the card refreshes with has_id_photo + id_photo_url (same
  // re-fetch path the retake flow uses). Falls back to the optimistic
  // capturedPreview thumbnail when the re-validation fails.
  const handleCaptureIdAndRefresh = useCallback(async (file: File | undefined | null) => {
    const ok = await handleIdFile(file);
    if (!ok || !scannedCode) return;
    setPhotoUpdated(false);
    await handleScanRef.current(scannedCode, { refreshOnly: true });  // FIX 2026-09-14: no scan event on photo refresh
    setPhotoUpdated(true);
  }, [handleIdFile, scannedCode]);

  const stopCamera = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (tickTimerRef.current !== null) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    zxingControlsRef.current?.stop();
    zxingControlsRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setScanning(false);
    setCameraState('off');
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setCameraState('starting');
    try {
      // Fallback path (iOS Safari, Firefox): no native BarcodeDetector — use the
      // wasm-based ZXing reader. It owns its own getUserMedia stream and decode
      // loop on the same <video> element.
      if (!barcodeDetectorSupported) {
        const video = videoRef.current;
        if (!video) return;
        const reader = new BrowserQRCodeReader();
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: 'environment' }, audio: false },
          video,
          (result) => {
            if (!result) return;
            // Result modal open (Jaime 2026-09-11): block further scans until
            // the guard explicitly closes the popup.
            if (resultModalOpenRef.current) return;
            const raw: string = result.getText();
            const now = Date.now();
            if (raw && !(raw === lastDecodedRef.current && now - lastDecodedAtRef.current < 3000)) {
              lastDecodedRef.current = raw;
              lastDecodedAtRef.current = now;
              handleScanRef.current(raw);
            }
          }
        );
        zxingControlsRef.current = controls;
        setScanning(true);
        setCameraState('active');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setScanning(true);
      setCameraState('active');

      const detector = new BarcodeDetectorCtor({ formats: ['qr_code'] });

      const detectLoop = async () => {
        const video = videoRef.current;
        if (!video || !streamRef.current || video.readyState < 2) {
          rafRef.current = requestAnimationFrame(detectLoop);
          return;
        }
        try {
          const codes = await detector.detect(video);
          if (codes.length > 0 && !resultModalOpenRef.current) {
            const raw: string = codes[0].rawValue || '';
            // Debounce: ignore the same code repeated within 3s
            const now = Date.now();
            if (raw && !(raw === lastDecodedRef.current && now - lastDecodedAtRef.current < 3000)) {
              lastDecodedRef.current = raw;
              lastDecodedAtRef.current = now;
              handleScanRef.current(raw);
            }
          }
        } catch {
          // transient decode errors are non-fatal
        }
        // Poll at ~10 fps instead of every frame to save battery
        tickTimerRef.current = window.setTimeout(detectLoop as unknown as () => void, 100);
      };
      detectLoop();
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        setCameraState('denied');
        setCameraError('Permiso de cámara denegado. Habilítalo en tu navegador o usa la entrada manual.');
      } else if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
        setCameraState('unavailable');
        setCameraError('No se encontró una cámara disponible. Usa la entrada manual.');
      } else {
        setCameraState('unavailable');
        setCameraError('No se pudo iniciar la cámara. Usa la entrada manual.');
      }
    }
  }, [handleScan]);

  // Start camera on mount, stop stream on unmount
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ruling 3(b) ID-capture flow (unregistered visitor), reused INSIDE the
  // result modal (Jaime 2026-09-11). Renders nothing when not applicable.
  const renderIdCapture = () => {
    if (!(card?.kind === 'visitor' && card.idScanRequired && card.status === 'allowed' && !card.hasIdPhoto)) return null;
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-800 border border-amber-500/40 p-6 mb-4">
        <h3 className="text-slate-900 dark:text-white font-bold mb-1 flex items-center gap-2">
          <IdCard className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          Registrar identificación del visitante
        </h3>
        <p className="text-slate-600 dark:text-gray-400 text-sm mb-4">
          Visitante sin perfil: captura su documento de identidad. Se guarda vinculado a la visita (retención ~30 días).
        </p>
        {idCaptureOk ? (
          <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 rounded-xl px-4 py-3">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">ID registrado</span>
          </div>
        ) : (
          <>
            <label className={`flex items-center justify-center gap-2 w-full py-4 rounded-2xl font-bold cursor-pointer transition-colors ${idCaptureBusy ? 'bg-slate-300 text-slate-500 dark:bg-gray-700 dark:text-gray-400' : 'bg-[#36CCD8] text-white hover:bg-[#2DB8C4]'}`}>
              {idCaptureBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
              {idCaptureBusy ? 'Subiendo…' : 'Capturar ID (cámara o archivo)'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={idCaptureBusy}
                onChange={(e) => {
                  handleCaptureIdAndRefresh(e.target.files?.[0]);
                  e.currentTarget.value = '';
                }}
              />
            </label>
            {idCaptureError && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-300">{idCaptureError}</p>
            )}
            <p className="mt-3 text-xs text-gray-500">
              {scannedCode
                ? `Se vinculará automáticamente al código escaneado (${scannedCode}).`
                : 'Introduce el código de acceso antes de capturar para vincular la identificación a la visita.'}
            </p>
          </>
        )}
      </div>
    );
  };

  // Scan-result modal (Jaime 2026-09-11): EVERY scan opens this blocking
  // popup with the typed result card (verdict banner, person info, direction,
  // escanear-doc prompt for unregistered visitors, ID-capture flow). The
  // camera decode loop and manual entry are gated while it is open. It only
  // closes via the explicit X / "Continuar" buttons — no timeout, no
  // backdrop-click dismissal. Closing resumes scanning.
  const renderScanResultModal = () => {
    if (!resultModalOpen || !card) return null;
    return (
      <div
        className="fixed inset-0 z-[90] bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label="Resultado del escaneo"
      >
        <div className="relative w-full max-w-md my-4">
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => { setResultModalOpen(false); setPhotoUpdated(false); setCapturedPreview(null); }}
            className="absolute top-2 right-2 z-10 w-10 h-10 rounded-full bg-gray-700/90 text-white text-2xl leading-none flex items-center justify-center border border-gray-500 shadow-lg"
          >
            ×
          </button>
          <ScanResultCard card={card} />
          {alreadyInside && (
            <div className="rounded-2xl bg-amber-500/15 border border-amber-500/50 p-4 mb-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 dark:text-amber-200 text-sm font-medium">
                Esta persona ya registró ENTRADA y no ha registrado SALIDA.
              </p>
            </div>
          )}
          {card.kind === 'visitor' && card.status === 'allowed' && card.hasIdPhoto && (
            <div className="rounded-2xl bg-white dark:bg-gray-800 border border-emerald-500/30 px-4 py-3 mb-4">
              <div className="flex items-center gap-2">
                <IdCard className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <p className="text-emerald-700 dark:text-emerald-300 text-sm">Identificación ya registrada</p>
              </div>
              {/* Jaime 2026-09-11: recapture path for unclear existing photos.
                  Secondary/muted styling so it does not compete with Continuar.
                  NOTE: the old guard included card.idScanRequired, which is
                  always false when hasIdPhoto is true — this block never
                  rendered before; fixed here. */}
              {photoUpdated ? (
                <div className="mt-3 flex items-center gap-2 text-emerald-700 dark:text-emerald-300 text-sm">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Foto actualizada</span>
                </div>
              ) : (
                <label
                  className={`mt-3 flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-slate-300 dark:border-gray-600 text-slate-700 dark:text-gray-300 text-sm font-semibold cursor-pointer transition-colors ${idCaptureBusy ? 'opacity-60' : 'hover:bg-slate-100 dark:hover:bg-gray-700/60'}`}
                >
                  {idCaptureBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  {idCaptureBusy ? 'Subiendo…' : 'Tomar nueva foto'}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={idCaptureBusy}
                    onChange={(e) => {
                      handleRecaptureId(e.target.files?.[0]);
                      e.currentTarget.value = '';
                    }}
                  />
                </label>
              )}
              {idCaptureError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-300">{idCaptureError}</p>
              )}
            </div>
          )}
          {renderIdCapture()}
          {/* FIX 2026-09-11 (Jaime): the photo taken in THIS modal must be
              visible immediately — first capture AND retake. Optimistic local
              thumbnail; the canonical photo renders via ScanResultCard's
              AuthImage once the re-validation returns id_photo_url. */}
          {capturedPreview && (
            <div className="rounded-2xl bg-white dark:bg-gray-800 border border-emerald-500/30 p-4 mb-4 flex items-center gap-3">
              <img
                src={capturedPreview}
                alt="Foto recién capturada del documento"
                className="w-28 h-20 object-cover rounded-lg border border-slate-300 dark:border-gray-600"
              />
              <div className="min-w-0">
                <p className="text-emerald-700 dark:text-emerald-300 text-sm font-semibold flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" /> Foto capturada
                </p>
                <p className="text-slate-500 dark:text-gray-400 text-xs mt-0.5">Guardada en el registro de la visita.</p>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => { setResultModalOpen(false); setPhotoUpdated(false); setCapturedPreview(null); }}
            className="w-full py-4 bg-[#36CCD8] text-white font-bold text-lg rounded-2xl hover:bg-[#2DB8C4] shadow-lg"
          >
            Continuar
          </button>
        </div>
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'allowed': return 'bg-emerald-500';
      case 'denied': return 'bg-red-500';
      case 'expired': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'allowed': return <CheckCircle className="w-8 h-8 text-white" />;
      case 'denied': return <XCircle className="w-8 h-8 text-white" />;
      case 'expired': return <AlertTriangle className="w-8 h-8 text-white" />;
      default: return <Shield className="w-8 h-8 text-white" />;
    }
  };

  const showCameraFeed = cameraState === 'active';

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] dark:bg-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="sticky top-0 bg-[var(--v-base-bg)] dark:bg-gray-900 border-b border-[var(--v-surface-3)] dark:border-gray-800 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white rounded-xl">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[var(--v-text-primary)]">Verificar QR</h1>
              <p className="text-xs text-slate-500 dark:text-gray-500">
                Escáner de acceso
                {/* ADDENDUM 2026-09-14b risk 3: wrong condo context → wrong
                    NO_PERMISSION_HERE denials; show the active condo always. */}
                {scannerCondoName ? ` · ${scannerCondoName}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#36CCD8]" />
            <span className="text-sm text-slate-600 dark:text-gray-400">{user?.name || 'Guardia'}</span>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-md mx-auto">
        {/* Scanner View */}
        <div className="relative aspect-square bg-slate-200 dark:bg-gray-800 rounded-3xl overflow-hidden mb-6">
          {/* Live camera feed (hidden until stream is active) */}
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className={`absolute inset-0 w-full h-full object-cover ${showCameraFeed ? '' : 'hidden'}`}
          />

          {/* Camera status overlay (starting / denied / unavailable / off) */}
          {!showCameraFeed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6">
                {cameraState === 'starting' ? (
                  <>
                    <Camera className="w-16 h-16 text-slate-400 dark:text-gray-500 mx-auto mb-4 animate-pulse" />
                    <p className="text-slate-600 dark:text-gray-400">Iniciando cámara…</p>
                  </>
                ) : cameraState === 'denied' ? (
                  <>
                    <CameraOff className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <p className="text-red-600 dark:text-red-600 dark:text-red-300 font-medium mb-2">Permiso de cámara denegado</p>
                    <p className="text-gray-500 text-sm mb-4">{cameraError}</p>
                    <button
                      onClick={startCamera}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-900 rounded-xl text-sm hover:bg-slate-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                    >
                      <RefreshCw className="w-4 h-4" /> Reintentar
                    </button>
                  </>
                ) : (
                  <>
                    <CameraOff className="w-16 h-16 text-slate-400 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-slate-500 dark:text-gray-500 mb-2">Cámara no disponible</p>
                    {cameraError && <p className="text-slate-500 dark:text-gray-600 text-sm mb-4">{cameraError}</p>}
                    <button
                      onClick={startCamera}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-900 rounded-xl text-sm hover:bg-slate-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                    >
                      <RefreshCw className="w-4 h-4" /> Reintentar
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Scan frame */}
          <div className="absolute inset-8 border-2 border-[#36CCD8] rounded-2xl pointer-events-none">
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#36CCD8] -mt-1 -ml-1" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#36CCD8] -mt-1 -mr-1" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#36CCD8] -mb-1 -ml-1" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#36CCD8] -mb-1 -mr-1" />
          </div>
          
          {/* Scan line animation (only while actively scanning) */}
          {showCameraFeed && (
            <div className="absolute inset-x-8 top-1/2 h-0.5 bg-[#36CCD8] shadow-[0_0_10px_#36CCD8] animate-pulse pointer-events-none" />
          )}
        </div>

        {/* Direction toggle (2026-09-09): ENTRADA (default) / SALIDA. The chosen
            direction is sent with the validate request and stamped on history. */}
        <div className="flex bg-slate-100 dark:bg-gray-800 rounded-2xl p-1 mb-4" role="tablist" aria-label="Dirección del escaneo">
          {(['entrada', 'salida'] as ScanDirection[]).map((d) => (
            <button
              key={d}
              role="tab"
              aria-selected={direction === d}
              onClick={() => setDirection(d)}
              className={`flex-1 py-2.5 rounded-xl font-bold text-sm uppercase tracking-wide transition-all ${
                direction === d
                  ? d === 'entrada'
                    ? 'bg-emerald-600 text-white shadow-lg'
                    : 'bg-amber-600 text-white shadow-lg'
                  : 'text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              {d === 'entrada' ? '⬆ Entrada' : '⬇ Salida'}
            </button>
          ))}
        </div>

        {/* Manual code entry — validates against the real endpoint (fallback + manual entry) */}
        <div className="flex gap-2 mb-6">
          <input
            type="text"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter' && !resultModalOpen) handleScan(); }}
            placeholder="Código de acceso (ej. A1B2C3D4)"
            className="flex-1 px-4 py-4 bg-white dark:bg-gray-800 text-slate-900 dark:text-white rounded-2xl border border-slate-300 dark:border-gray-700 focus:border-[#36CCD8] focus:outline-none placeholder-slate-400 dark:placeholder-gray-500"
          />
          <button
            onClick={() => handleScan()}
            disabled={!manualCode.trim() || resultModalOpen}
            className="px-6 py-4 bg-[#36CCD8] text-white font-bold rounded-2xl hover:bg-[#2DB8C4] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <QrCode className="w-5 h-5" /> Verificar
          </button>
        </div>

        {/* Scan result now renders in the blocking modal (Jaime 2026-09-11):
            renderScanResultModal() below. Inline result view intentionally
            removed to avoid a duplicate ID-capture flow. */}
        {/* Walk-in "Sin QR" (roadmap item E): register a visitor without a QR
            and track pending host approvals. Guard context only. */}
        <button
          type="button"
          onClick={() => setWalkInModalOpen(true)}
          disabled={!scannerCondoId}
          className="w-full py-4 mb-4 bg-white dark:bg-gray-800 border-2 border-[#36CCD8]/60 text-slate-900 dark:text-white font-bold rounded-2xl hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <UserPlus className="w-5 h-5 text-[#36CCD8]" /> Visitante sin QR
        </button>

        {/* Guard walk-in list: status filter tabs + WALK_IN_TIMEOUT reasons. */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6">
          <h3 className="text-slate-900 dark:text-white font-bold mb-4">Visitas sin QR</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {walkInTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setWalkInTab(t.key)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                  walkInTab === t.key ? 'bg-[#36CCD8] text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {t.label}
              </button>
            ))}
            <button
              type="button"
              onClick={loadWalkIns}
              className="ml-auto p-1.5 text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white rounded-lg"
              title="Recargar"
            >
              <RefreshCw className={`w-4 h-4 ${walkInsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {walkInsError && <p className="text-red-600 dark:text-red-300 text-sm mb-3">{walkInsError}</p>}
          {filteredWalkIns.length === 0 ? (
            <p className="text-slate-500 dark:text-gray-500 text-sm">No hay visitas en este estado.</p>
          ) : (
            <div className="space-y-3">
              {filteredWalkIns.map((w) => {
                const eff = walkInEffectiveStatus(w);
                const badge = WALK_IN_STATUS_BADGE[eff];
                return (
                  <div key={w.id} className="p-3 bg-slate-100 dark:bg-gray-700 rounded-xl flex items-start justify-between gap-3">
                    {/* Walk-in enhancement (Jaime 2026-09-16): ID photo thumbnail; tap to zoom. */}
                    {w.visitor_photo && (
                      <img
                        src={w.visitor_photo}
                        alt="Foto ID"
                        className="w-11 h-11 rounded-full object-cover flex-shrink-0 cursor-zoom-in"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        onClick={() => setWalkInPhotoZoom(w.visitor_photo || null)}
                      />
                    )}
                    <div className="min-w-0">
                      <p className="text-slate-900 dark:text-white font-medium truncate">{w.visitor_name || 'Visitante'}</p>
                      {/* FIX 2026-09-16: guard list rows now carry unit_id/
                          unit_label — show the real unit label instead of the
                          bare "Unidad" placeholder when present. */}
                      <p className="text-slate-600 dark:text-gray-400 text-xs">
                        {[
                          w.unit_label ? `Unidad ${w.unit_label}` : '',
                          w.visitor_id_number ? `ID: ${w.visitor_id_number}` : '',
                          w.phone || '',
                          w.plate || '',
                          w.reason || '',
                        ].filter(Boolean).join(' · ')}
                      </p>
                      {eff === 'timeout_cancelled' && (
                        <p className="text-red-600/80 dark:text-red-600 dark:text-red-300/80 text-xs">Cancelado por falta de aprobación (15 min) — WALK_IN_TIMEOUT</p>
                      )}
                      {w.access_code && (
                        <p className="text-slate-500 dark:text-gray-500 text-xs font-mono">{w.access_code}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Scan History */}
        {scanHistory.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6">
            <h3 className="text-slate-900 dark:text-white font-bold mb-4">Historial de Escaneos</h3>
            <div className="space-y-3">
              {scanHistory.map((scan, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 p-3 bg-slate-100 dark:bg-gray-700 rounded-xl">
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Subject photo thumbnail (2026-09-09): circle with initial fallback. */}
                    {scan.photo ? (
                      <img
                        src={scan.photo}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${getStatusColor(scan.status)}`}>
                        {scan.name?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-slate-900 dark:text-white font-medium truncate">{scan.name}</p>
                        {/* Direction badge (2026-09-09): ⬆ Entrada green / ⬇ Salida amber. */}
                        {scan.direction && (
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${scan.direction === 'entrada' ? 'bg-emerald-500/25 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/25 text-amber-700 dark:text-amber-300'}`}>
                            {scan.direction === 'entrada' ? '⬆ Entrada' : '⬇ Salida'}
                          </span>
                        )}
                        {/* Roadmap item E: code registered by the guard without a QR → "Sin QR" chip. */}
                        {scan.origin === 'walk_in' && (
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-violet-500/25 text-violet-700 dark:text-violet-300">
                            Sin QR
                          </span>
                        )}
                      </div>
                      {/* FIX 2026-09-09: ES entry-type label + destination from the
                          normalized card; visitors show "Visitante · <destino>" and
                          a "Destino:" line — never "Visitante · N/A". */}
                      <p className="text-slate-600 dark:text-gray-400 text-xs">
                        {HISTORY_TYPE_LABEL[scan.type]}
                        {scan.unit && scan.unit !== 'N/A' ? ` · ${scan.unit}` : ''}
                      </p>
                      {scan.unit && scan.unit !== 'N/A' && (
                        <p className="text-slate-500 dark:text-gray-500 text-xs flex items-center gap-1">
                          <Home className="w-3 h-3" /> Destino: {scan.unit}
                        </p>
                      )}
                      {scan.scannerName && (
                        <p className="text-slate-500 dark:text-gray-500 text-xs">
                          Escaneado por {scan.scannerName}{scannerRoleLabel(scan.scannerRole) ? ` · ${scannerRoleLabel(scan.scannerRole)}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* es-VE style dd/mm hh:mm (2026-09-09). */}
                  <span className="text-gray-400 text-xs whitespace-nowrap flex-shrink-0">
                    {fmtScanDateTime(scan.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      {/* Blocking scan-result modal (Jaime 2026-09-11) */}
      {renderScanResultModal()}
      {/* Walk-in enhancement (Jaime 2026-09-16): full-screen photo lightbox. */}
      {walkInPhotoZoom && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          role="dialog"
          aria-modal="true"
          aria-label="Foto de identificación ampliada"
          onClick={() => setWalkInPhotoZoom(null)}
        >
          <img src={walkInPhotoZoom} alt="Foto de identificación ampliada" className="max-w-full max-h-full rounded-xl object-contain" />
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => setWalkInPhotoZoom(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-800 text-white text-xl flex items-center justify-center border border-slate-300 dark:border-gray-600"
          >
            ×
          </button>
        </div>
      )}

      {/* Walk-in "Sin QR" registration modal (roadmap item E) */}
      <WalkInRegisterModal
        open={walkInModalOpen}
        onClose={() => setWalkInModalOpen(false)}
        condoId={scannerCondoId}
        onCreated={() => loadWalkIns()}
      />
    </div>
  );
}
